<template>
  <div>
    <b-card v-if="subscription" class="billing animated fadeIn">
      <h1>Subscription Details</h1>
      <b-alert v-if="subscription.status === 'canceled'" show variant="warning">
        This subscription has been canceled
      </b-alert>

      <section class="mb-5">
        <h3 class="mt-5">Details</h3>
        <p>Plan: {{ subscription.plan.nickname }}</p>
        <p>
          Total: {{ total | currency }} /
          {{ subscription.plan.interval }}
        </p>
        <p v-if="discount">Discount Details: {{ discount }}</p>
        <p>
          Next Invoice: {{ (subscription.current_period_end * 1000) | date }}
        </p>
        <p class="mb-0">
          Product:
          <i :class="icon"></i>
          {{ subscription.licenses[0].type | capitalize }}
        </p>
        <template v-if="features.length">
          <p class="mb-0">Features:</p>
          <ul>
            <li v-for="(feature, i) in features" :key="i">{{ feature }}</li>
          </ul>
        </template>
      </section>

      <h3>Licenses</h3>
      <b-table
        :items="licenses"
        :fields="licenseFields"
        :current-page="licenseCurrentPage"
        :per-page="perPage"
        responsive="sm"
        striped
        class="mb-5"
      >
        <template #cell(username)="cell">
          {{ cell.item.username || "-" }}
        </template>
        <template #cell(email)="cell">
          {{ cell.item.email || "-" }}
        </template>
        <template #cell(status)="cell">
          <b-badge :variant="cell.item.badgeVariant">
            {{ cell.item.status }}
          </b-badge>
        </template>
      </b-table>

      <b-row align-h="between">
        <b-col cols="auto">
          <b-link :to="{ name: 'Licenses' }" class="btn btn-primary">
            Manage Licenses
          </b-link>
        </b-col>
        <b-col cols="auto">
          <b-btn
            v-if="subscription.status !== 'canceled'"
            @click="showModal = !showModal"
            variant="link"
          >
            <span class="text-danger">Cancel</span>
          </b-btn>
        </b-col>
      </b-row>
    </b-card>

    <b-modal
      v-model="showModal"
      @hide="cancelText = ''"
      centered
      hide-header
      hide-footer
    >
      <p>
        Canceling today means that you will lose any premium features unlocked
        with your current subscription. This cannot be undone.
      </p>
      <p>Are you sure you want to lose access to your premium features?</p>

      <form @submit.prevent="onCancel">
        <label>
          Reason for cancelling:
          <b-form-select
            v-model="cancelSelected"
            :options="cancelOptions"
            class="mb-3"
          >
            <template #first>
              <option :value="null" disabled></option>
            </template>

            <option value="other">Other</option>
          </b-form-select>
        </label>

        <label v-if="cancelSelected === 'other'">
          More details:
          <b-form-textarea
            v-model="cancelReason"
            :rows="3"
            :max-rows="6"
            aria-label="Why are you cancelling?"
            class="mb-3"
          />
        </label>

        <label>
          To continue, please type
          <code>"CANCEL"</code> into the box below:
          <b-form-input v-model="cancelText" class="mb-3" />
        </label>

        <b-btn :disabled="$v.$invalid" type="submit" variant="danger">
          Confirm Cancellation
        </b-btn>
      </form>
    </b-modal>
  </div>
</template>

<script>
import shuffle from "lodash.shuffle";
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";

import API from "@/api";
import productDetails from "@/data/products";
import { calcTotalPrice } from "@/helpers/pricing";

const cancelOptions = [
  "Poor Customer Service",
  "Too few features",
  "Too many Features",
  "Pricing"
];

export default {
  metaInfo: {
    titleTemplate: "Subscriptions - %s"
  },

  mixins: [validationMixin],

  props: {
    subscriptionId: {
      type: String,
      required: true
    }
  },

  data: () => ({
    subscription: null,
    licenseFields: [
      { key: "username", sortable: true },
      { key: "email", sortable: true },
      { key: "status", sortable: true }
    ],
    licenseCurrentPage: 1,
    perPage: 50,
    showModal: false,
    cancelSelected: null,
    cancelOptions: shuffle(cancelOptions),
    cancelReason: "",
    cancelText: ""
  }),

  computed: {
    icon() {
      return productDetails[this.subscription.licenses[0].type].icon;
    },

    features() {
      return productDetails[this.subscription.licenses[0].type].features;
    },

    discount() {
      if (!this.subscription || !this.subscription.discount) return "";

      const coupon = this.subscription.discount.coupon;
      let amountOff = "";
      let duration;

      if (coupon.amount_off) {
        if (coupon.currency === "usd") {
          amountOff = `$${coupon.amount_off / 100}`;
        } else {
          amountOff = `${coupon.amount_off / 100} ${coupon.currency}`;
        }
      } else if (coupon.percent_off) {
        amountOff = `${coupon.percent_off}%`;
      }

      if (coupon.duration === "forever") {
        duration = "";
      } else if (coupon.duration === "repeating") {
        duration = ` for ${coupon.duration_in_months} month/s`;
      } else if (coupon.duration === "once") {
        duration = ` for the first billing cycle`;
      }

      return `${amountOff} off` + `${duration}`;
    },

    licenses() {
      return this.subscription.licenses.map(license => {
        const badgeVariants = {
          accepted: "success",
          pending: "warning",
          expired: "danger"
        };
        if (license.status === "INUSE") {
          license.status = "accepted";
        } else if (license.status === "WAITING") {
          license.status = "pending";
        }
        license.status = license.status.toLowerCase();
        return {
          ...license,
          username: license.using_user_name,
          email: license.using_user_email || license.using_invited,
          badgeVariant: badgeVariants[license.status] || "secondary"
        };
      });
    },

    total() {
      const { plan, quantity } = this.subscription;
      return calcTotalPrice(plan, quantity) / 100;
    }
  },

  async created() {
    this.$store.commit("REQUEST_INCREMENT");

    const response = await API.getSubscription({
      subscriptionId: this.subscriptionId
    });

    if (response.success) {
      this.subscription = response.results;
    }

    this.$store.commit("REQUEST_DECREMENT");
  },

  methods: {
    async onCancel() {
      if (this.$v.$invalid) return;

      this.showModal = false;
      this.$store.commit("REQUEST_INCREMENT");

      // Send contact form asynchronously
      const { email, name, unique } = this.$store.state.user;

      const data = {
        "Notification Type": "Subscription Cancellation",
        "Customer Email": email,
        "Customer Username": name,
        "Customer ID": unique,
        "Cancel Reason":
          this.cancelSelected === "other"
            ? this.cancelReason
            : this.cancelSelected
      };

      const response = await API.cancelSubscription({
        subscriptionId: this.subscriptionId,
        data
      });

      if (response.success) {
        this.subscription = null;
        this.$router.push("/billing");
      }

      this.$store.commit("REQUEST_DECREMENT");
    }
  },

  validations: {
    cancelSelected: {
      required
    },
    cancelReason: {
      required: requiredIf(vm => vm.cancelSelected === "other")
    },
    cancelText: {
      matches: value => value === "CANCEL"
    }
  }
};
</script>

<style></style>
